import { client } from "../../amplify-client";
import {
  getLatestIncomingMessageSk,
  hasUnreadIncomingSince,
  isConversationReadSupported,
  listConversationReadMarkers,
} from "./conversation-read-service";
import {
  isActiveListConversation,
  isPastListConversation,
  isPendingRequestExpired,
  shouldShowPingPending,
} from "./conversation-state";
import {
  isUserOnline,
  listPresenceByUserIds,
} from "../presence/presence-service";
import { getProfileSummaries } from "../profile/profile-summary-service";
import { listHiddenConversationsForUser } from "./conversation-visibility-service";
import { deleteChatLifecycle } from "./chat-lifecycle-service";

const snapshotCacheByUserId = new Map();
const inFlightByUserId = new Map();
const projectionBackfillAttemptedByUserId = new Set();
const expiredPendingCleanupAttemptByChatId = new Map();

const MAX_FORCE_REFRESH_PER_USER_MS = 2_500;
const EXPIRED_PENDING_CLEANUP_THROTTLE_MS = 60_000;

function normalizeText(value) {
  return String(value || "").trim();
}

function snapshotIncludesChatPk(snapshot, chatPk) {
  if (!chatPk) return true;
  if (!snapshot) return false;
  const lists = [
    snapshot.activeChats,
    snapshot.pendingRequests,
    snapshot.pingReceivedRequests,
    snapshot.pingAcceptedRequests,
    snapshot.pastChats,
  ];
  return lists.some(
    (list) =>
      Array.isArray(list) &&
      list.some((entry) => normalizeText(entry?.chatPk) === chatPk)
  );
}

function sortDescString(a, b) {
  return String(b ?? "").localeCompare(String(a ?? ""));
}

function formatTimeLabel(isoString) {
  const t = new Date(isoString);
  if (Number.isNaN(t.getTime())) return "";
  const now = Date.now();
  const diff = now - t.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  return `${w}w ago`;
}

function safeInitials(nameOrEmail) {
  if (!nameOrEmail) return "U";
  const parts = nameOrEmail.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nameOrEmail.slice(0, 2).toUpperCase();
}

function isWithinHours(isoString, nowMs = Date.now(), hours = 24) {
  const ts = new Date(isoString).getTime();
  if (Number.isNaN(ts)) return false;
  return nowMs - ts <= hours * 60 * 60 * 1000;
}

function statusRankForActive(row, mySenderId) {
  const status = row?.status || "accepted";
  if (status === "accepted") return 3;
  if (status === "pending" && row?.initiated_by === mySenderId) return 2;
  if (status === "pending") return 1;
  return 0;
}

function shouldAttemptExpiredPendingCleanup(chatPk, nowMs = Date.now()) {
  const normalizedChatPk = normalizeText(chatPk);
  if (!normalizedChatPk) return false;
  const lastAttemptAt = Number(expiredPendingCleanupAttemptByChatId.get(normalizedChatPk) || 0);
  if (nowMs - lastAttemptAt < EXPIRED_PENDING_CLEANUP_THROTTLE_MS) return false;
  expiredPendingCleanupAttemptByChatId.set(normalizedChatPk, nowMs);
  return true;
}

async function cleanupExpiredPendingChats(chatIds = []) {
  const nowMs = Date.now();
  const uniqueChatIds = [...new Set((chatIds || []).map((value) => normalizeText(value)).filter(Boolean))];
  await Promise.all(
    uniqueChatIds.map(async (chatPk) => {
      if (!shouldAttemptExpiredPendingCleanup(chatPk, nowMs)) return;
      try {
        await deleteChatLifecycle(chatPk);
      } catch (error) {
        console.warn("Failed to clean up expired pending request:", { chatPk, error });
      }
    })
  );
}

function buildCounterpartProfile(counterpartId, counterpartProfile, fallbackTitle) {
  if (counterpartProfile?.profile) {
    return {
      ...counterpartProfile.profile,
      cognitoId: counterpartProfile.profile.cognitoId || counterpartId || null,
    };
  }

  return {
    cognitoId: counterpartId || null,
    profileName: counterpartProfile?.profileName || fallbackTitle || "User",
    profilePicture: counterpartProfile?.profilePicture || "",
  };
}

function buildSyntheticUserChatRowFromChat({ chatPk, chat, mySenderId }) {
  const normalizedChatPk = normalizeText(chatPk);
  if (!normalizedChatPk || !chat) return null;

  const status = normalizeText(chat?.status || "accepted") || "accepted";
  const initiatedBy = normalizeText(chat?.request_note_from);
  const activityAt = normalizeText(chat?.updatedAt || chat?.createdAt || new Date().toISOString());
  const rowName = normalizeText(chat?.name) || "Chat";

  return {
    PK: `CONV#USER#${mySenderId}`,
    SK: activityAt,
    chat_id: normalizedChatPk,
    isGroup: Boolean(chat?.isGroup),
    name: rowName,
    user_id: mySenderId,
    status,
    initiated_by: initiatedBy,
  };
}

function buildEmptySnapshot(userId) {
  return {
    userId: normalizeText(userId) || null,
    latestSyncedAt: new Date().toISOString(),
    activeChats: [],
    pendingRequests: [],
    pingReceivedRequests: [],
    pingAcceptedRequests: [],
    pastChats: [],
  };
}

// Member-gated single-chat fetch. Direct Chat.list/get from clients is denied
// at the model level — only chat_members can read a Chat row, enforced by the
// chatRead lambda behind getMyChat.
async function fetchChatByPk(chatPk) {
  const resp = await client.queries.getMyChat({ chatPk });
  const errors = Array.isArray(resp?.errors) ? resp.errors : [];
  if (errors.length) {
    throw new Error(errors.map((err) => err.message).join(", "));
  }
  return resp?.data ?? null;
}

function dedupeUserChatRowsByChatId(rows, mySenderId) {
  const byChatId = new Map();
  for (const row of rows || []) {
    const chatId = normalizeText(row?.chat_id);
    if (!chatId) continue;

    const existing = byChatId.get(chatId);
    if (!existing) {
      byChatId.set(chatId, row);
      continue;
    }

    const existingRank = statusRankForActive(existing, mySenderId);
    const nextRank = statusRankForActive(row, mySenderId);
    if (nextRank > existingRank) {
      byChatId.set(chatId, row);
      continue;
    }
    if (nextRank === existingRank && String(row?.SK ?? "") > String(existing?.SK ?? "")) {
      byChatId.set(chatId, row);
    }
  }

  return Array.from(byChatId.values());
}

async function listAllUserChats(mySenderId) {
  const PK = `CONV#USER#${mySenderId}`;
  let nextToken = undefined;
  const rows = [];

  do {
    const resp = await client.models.UserChat.list({
      filter: { PK: { eq: PK } },
      limit: 200,
      nextToken,
    });
    const { data, errors } = resp ?? {};
    if (errors?.length) {
      throw new Error(errors.map((entry) => entry?.message).join(", "));
    }
    rows.push(...(data ?? []));
    nextToken = resp?.nextToken;
  } while (nextToken);

  return rows;
}

function hasInboxProjectionModel() {
  const model = client?.models?.UserInboxItem;
  return Boolean(
    model &&
      typeof model.list === "function" &&
      typeof model.create === "function" &&
      typeof model.update === "function" &&
      (typeof model.get === "function" || typeof model.list === "function")
  );
}

function normalizeInteger(value, fallback = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.floor(numeric));
}

function maxIso(leftIso, rightIso) {
  const left = normalizeText(leftIso);
  const right = normalizeText(rightIso);
  if (!left) return right;
  if (!right) return left;
  return left > right ? left : right;
}

async function listAllUserInboxItems(userId) {
  const normalizedUserId = normalizeText(userId);
  if (!normalizedUserId || !hasInboxProjectionModel()) return [];

  let nextToken = undefined;
  const rows = [];
  do {
    const resp = await client.models.UserInboxItem.list({
      filter: { user_id: { eq: normalizedUserId } },
      limit: 200,
      nextToken,
    });
    const { data, errors } = resp ?? {};
    if (errors?.length) {
      throw new Error(errors.map((entry) => entry?.message).join(", "));
    }
    rows.push(...(data ?? []));
    nextToken = resp?.nextToken;
  } while (nextToken);

  return rows;
}

function dedupeInboxRowsByChatId(rows, mySenderId) {
  const dedupedByChatId = new Map();
  for (const row of rows || []) {
    const chatId = normalizeText(row?.chat_id);
    if (!chatId) continue;

    const normalizedLastActivityAt = normalizeText(
      row?.last_activity_at || row?.updated_at || row?.updatedAt || row?.createdAt || ""
    );
    const normalizedStatus = normalizeText(row?.status || "accepted") || "accepted";
    const normalizedRow = {
      ...row,
      status: normalizedStatus,
      last_activity_at: normalizedLastActivityAt,
      initiated_by: normalizeText(row?.initiated_by),
    };

    const existing = dedupedByChatId.get(chatId);
    if (!existing) {
      dedupedByChatId.set(chatId, normalizedRow);
      continue;
    }

    const existingRank = statusRankForActive(
      { status: existing.status, initiated_by: existing.initiated_by },
      mySenderId
    );
    const nextRank = statusRankForActive(
      { status: normalizedRow.status, initiated_by: normalizedRow.initiated_by },
      mySenderId
    );
    if (nextRank > existingRank) {
      dedupedByChatId.set(chatId, normalizedRow);
      continue;
    }
    if (
      nextRank === existingRank &&
      String(normalizedRow?.last_activity_at || "") > String(existing?.last_activity_at || "")
    ) {
      dedupedByChatId.set(chatId, normalizedRow);
    }
  }

  return Array.from(dedupedByChatId.values());
}

function toConversationStateRowFromInboxRow(row) {
  return {
    status: normalizeText(row?.status || "accepted") || "accepted",
    initiated_by: normalizeText(row?.initiated_by),
    SK: normalizeText(row?.last_activity_at || row?.updated_at || row?.createdAt || ""),
    request_created_at: normalizeText(row?.request_created_at || row?.createdAt || ""),
  };
}

async function fetchCommunicationInboxSnapshotLegacy({ userId, selectedChatPk = null }) {
  const mySenderId = normalizeText(userId);
  if (!mySenderId) return buildEmptySnapshot(userId);

  const nowMs = Date.now();
  const supportsConversationRead = isConversationReadSupported();
  let lastSeenActiveTimestamp = "";
  let readMarkersByChatId = new Map();

  if (supportsConversationRead) {
    readMarkersByChatId = await listConversationReadMarkers(mySenderId);
  } else {
    try {
      const userResp = await client.models.User.get({ userId: mySenderId });
      lastSeenActiveTimestamp = normalizeText(userResp?.data?.last_viewed_active);
    } catch (error) {
      console.error("Failed to load last viewed active timestamp:", error);
    }
  }

  const hiddenConversationIds = await listHiddenConversationsForUser(mySenderId);
  const rows = await listAllUserChats(mySenderId);
  const dedupedRows = dedupeUserChatRowsByChatId(rows, mySenderId);

  const selectedChatCandidate = normalizeText(selectedChatPk);
  const chatById = new Map();
  await Promise.all(
    dedupedRows.map(async (row) => {
      const chatId = normalizeText(row?.chat_id);
      if (!chatId) return;
      try {
        const chat = await fetchChatByPk(chatId);
        if (chat) chatById.set(chatId, chat);
      } catch (error) {
        console.error("Failed to resolve chat by id:", { chatId, error });
      }
    })
  );

  const selectedAlreadyPresent = selectedChatCandidate
    ? dedupedRows.some((row) => normalizeText(row?.chat_id) === selectedChatCandidate)
    : false;

  if (selectedChatCandidate && !selectedAlreadyPresent) {
    try {
      const selectedChat = await fetchChatByPk(selectedChatCandidate);
      const members = Array.isArray(selectedChat?.chat_members)
        ? selectedChat.chat_members
        : [];
      const status = normalizeText(selectedChat?.status).toLowerCase();
      const isAllowedStatus = status === "accepted" || status === "pending";
      if (selectedChat && members.includes(mySenderId) && isAllowedStatus) {
        const syntheticRow = buildSyntheticUserChatRowFromChat({
          chatPk: selectedChatCandidate,
          chat: selectedChat,
          mySenderId,
        });
        if (syntheticRow) {
          dedupedRows.push(syntheticRow);
          chatById.set(selectedChatCandidate, selectedChat);
        }
      }
    } catch (error) {
      console.warn("Unable to synthesize selected chat for active list:", error);
    }
  }

  const counterpartByChatId = new Map();
  dedupedRows.forEach((row) => {
    const chatId = normalizeText(row?.chat_id);
    const chat = chatById.get(chatId);
    const members = Array.isArray(chat?.chat_members) ? chat.chat_members : [];
    const counterpartId = members.find((memberId) => memberId !== mySenderId) || null;
    counterpartByChatId.set(chatId, counterpartId);
  });

  const counterpartIds = Array.from(counterpartByChatId.values()).filter(Boolean);
  const profileSummaryByUserId = await getProfileSummaries(counterpartIds);
  const presenceByUserId = await listPresenceByUserIds(counterpartIds);

  const activeChats = [];
  const unreadCandidates = [];
  const pingReceivedRequests = [];
  const pingAcceptedRequests = [];
  const pastChats = [];
  const expiredPendingChatIds = new Set();

  for (const row of dedupedRows) {
    const chatPk = normalizeText(row?.chat_id);
    if (!chatPk) continue;

    const isSelectedCandidate = selectedChatCandidate && chatPk === selectedChatCandidate;
    if (hiddenConversationIds.has(chatPk) && !isSelectedCandidate) continue;

    const chat = chatById.get(chatPk);
    if (!chat) continue;

    const members = Array.isArray(chat?.chat_members) ? chat.chat_members : [];
    if (members.length && !members.includes(mySenderId)) continue;

    const status = normalizeText(chat?.status || row?.status || "accepted") || "accepted";
    const initiatedBy = normalizeText(row?.initiated_by);
    const isInitiatedByMe = initiatedBy === mySenderId;
    const isGroupChat = Boolean(chat?.isGroup || row?.isGroup);
    const requestMessage = normalizeText(chat?.discussion_topic || chat?.request_note || "");
    const requestMessageFrom = normalizeText(chat?.request_note_from || initiatedBy || "");
    const requestCreatedAt = normalizeText(chat?.createdAt || row?.SK || "");
    const lastUpdatedAt = normalizeText(row?.SK);
    const counterpartId = counterpartByChatId.get(chatPk) || null;
    const counterpartProfile = counterpartId
      ? profileSummaryByUserId.get(counterpartId)
      : null;
    const title = normalizeText(row?.name) || counterpartProfile?.profileName || "Chat";
    const counterpartPresence = counterpartId ? presenceByUserId.get(counterpartId) || null : null;
    const lastSeenAt = counterpartPresence?.last_seen_at || null;
    const presenceStatus = isUserOnline(lastSeenAt) ? "online" : "offline";
    const normalizedRow = {
      ...row,
      status,
      SK: lastUpdatedAt,
      request_created_at: requestCreatedAt,
    };
    if (isPendingRequestExpired(normalizedRow, nowMs)) {
      if (!isGroupChat) {
        expiredPendingChatIds.add(chatPk);
      }
      continue;
    }

    if (isActiveListConversation(normalizedRow, mySenderId, nowMs)) {
      const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
      activeChats.push({
        chatPk,
        title,
        isGroup: isGroupChat,
        lastUpdatedAt,
        timeLabel: formatTimeLabel(lastUpdatedAt),
        avatar: safeInitials(title),
        profilePicture: counterpartProfile?.profilePicture || "",
        unreadCount: 0,
        previewText: requestMessage || "",
        status,
        initiatedBy,
        isInitiatedByMe,
        requestMessage,
        requestMessageFrom,
        requestCreatedAt,
        counterpartId: counterpartId || null,
        counterpartProfile: profileRecord,
        lastSeenAt,
        presenceStatus,
      });

      if (status === "accepted") {
        const fallbackMarker = String(lastSeenActiveTimestamp || "");
        const markerEntry = readMarkersByChatId.get(chatPk);
        const markerFromReadModel = String(markerEntry?.lastViewedMessageSk || "");
        const unreadMarker = supportsConversationRead ? markerFromReadModel : fallbackMarker;
        unreadCandidates.push({ chatPk, unreadMarker });
      }
    }

    if (shouldShowPingPending(normalizedRow, mySenderId, nowMs)) {
      const requestActivityAt = normalizeText(chat?.createdAt || row?.SK);
      const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
      pingReceivedRequests.push({
        chatPk,
        title,
        isGroup: isGroupChat,
        lastUpdatedAt: requestActivityAt,
        timeLabel: formatTimeLabel(requestActivityAt),
        avatar: safeInitials(title),
        profilePicture: counterpartProfile?.profilePicture || "",
        status,
        discussionTopic: requestMessage,
        initiatedBy,
        isInitiatedByMe,
        counterpartId: counterpartId || null,
        counterpartProfile: profileRecord,
      });
    }

    if (status === "accepted" && isInitiatedByMe) {
      const requestActivityAt = normalizeText(chat?.updatedAt || row?.SK);
      if (isWithinHours(requestActivityAt, nowMs, 24)) {
        const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
        pingAcceptedRequests.push({
          chatPk,
          title,
          isGroup: isGroupChat,
          lastUpdatedAt: requestActivityAt,
          timeLabel: formatTimeLabel(requestActivityAt),
          avatar: safeInitials(title),
          profilePicture: counterpartProfile?.profilePicture || "",
          status: "accepted",
          discussionTopic: requestMessage,
          initiatedBy: mySenderId,
          isInitiatedByMe: true,
          counterpartId: counterpartId || null,
          counterpartProfile: profileRecord,
        });
      }
    }

    if (isPastListConversation(normalizedRow, nowMs)) {
      const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
      pastChats.push({
        id: chatPk,
        chatPk,
        name: title,
        title,
        isGroup: isGroupChat,
        lastUpdatedAt,
        timestamp: formatTimeLabel(lastUpdatedAt),
        timeLabel: formatTimeLabel(lastUpdatedAt),
        avatar: safeInitials(title),
        profilePicture: counterpartProfile?.profilePicture || "",
        lastMessage: "",
        previewText: "",
        counterpartId: counterpartId || null,
        counterpartProfile: profileRecord,
      });
    }
  }

  const unreadResults = await Promise.all(
    unreadCandidates.map(async ({ chatPk, unreadMarker }) => {
      const latestIncomingMessageSk = await getLatestIncomingMessageSk(chatPk, mySenderId);
      const hasUnread = hasUnreadIncomingSince(unreadMarker, latestIncomingMessageSk);
      return [chatPk, hasUnread];
    })
  );
  const unreadByChatId = new Map(unreadResults);

  const activeChatsWithUnread = activeChats.map((chat) => ({
    ...chat,
    unreadCount:
      chat.status === "pending" && !chat.isInitiatedByMe
        ? 1
        : unreadByChatId.get(chat.chatPk)
          ? 1
          : 0,
  }));

  activeChatsWithUnread.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));
  pingReceivedRequests.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));
  pingAcceptedRequests.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));
  pastChats.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));

  if (expiredPendingChatIds.size > 0) {
    void cleanupExpiredPendingChats(Array.from(expiredPendingChatIds));
  }

  return {
    userId: mySenderId,
    latestSyncedAt: new Date().toISOString(),
    activeChats: activeChatsWithUnread,
    pendingRequests: [],
    pingReceivedRequests,
    pingAcceptedRequests,
    pastChats,
  };
}

function statusRank(status) {
  const normalizedStatus = normalizeText(status);
  if (normalizedStatus === "pending") return 2;
  if (normalizedStatus === "accepted") return 1;
  return 0;
}

function mergeProjectionCandidate(existing, incoming) {
  if (!existing) return { ...incoming };

  return {
    chatId: normalizeText(existing.chatId || incoming.chatId),
    status:
      statusRank(incoming.status) >= statusRank(existing.status)
        ? normalizeText(incoming.status || "accepted") || "accepted"
        : normalizeText(existing.status || "accepted") || "accepted",
    initiatedBy: normalizeText(incoming.initiatedBy) || normalizeText(existing.initiatedBy),
    isGroup: Boolean(existing.isGroup || incoming.isGroup),
    counterpartId:
      normalizeText(incoming.counterpartId) || normalizeText(existing.counterpartId),
    counterpartName:
      normalizeText(incoming.counterpartName) || normalizeText(existing.counterpartName),
    requestMessage:
      normalizeText(incoming.requestMessage) || normalizeText(existing.requestMessage),
    requestMessageFrom:
      normalizeText(incoming.requestMessageFrom) || normalizeText(existing.requestMessageFrom),
    requestCreatedAt:
      normalizeText(incoming.requestCreatedAt) || normalizeText(existing.requestCreatedAt),
    lastActivityAt: maxIso(existing.lastActivityAt, incoming.lastActivityAt),
    previewText: normalizeText(incoming.previewText) || normalizeText(existing.previewText),
    lastMessageFrom:
      normalizeText(incoming.lastMessageFrom) || normalizeText(existing.lastMessageFrom),
    unreadCount: Math.max(
      normalizeInteger(existing.unreadCount, 0),
      normalizeInteger(incoming.unreadCount, 0)
    ),
  };
}

function collectProjectionBackfillCandidates(snapshot) {
  const byChatId = new Map();

  function upsertCandidate(candidate) {
    const chatId = normalizeText(candidate?.chatId);
    if (!chatId) return;
    const normalizedCandidate = {
      ...candidate,
      chatId,
      status: normalizeText(candidate?.status || "accepted") || "accepted",
      lastActivityAt: normalizeText(candidate?.lastActivityAt || ""),
      unreadCount: normalizeInteger(candidate?.unreadCount, 0),
    };
    const existing = byChatId.get(chatId);
    byChatId.set(chatId, mergeProjectionCandidate(existing, normalizedCandidate));
  }

  for (const chat of snapshot?.activeChats || []) {
    upsertCandidate({
      chatId: chat?.chatPk,
      status: chat?.status || "accepted",
      initiatedBy: chat?.initiatedBy,
      isGroup: Boolean(chat?.isGroup),
      counterpartId: chat?.counterpartId,
      counterpartName: chat?.title,
      requestMessage: chat?.requestMessage || chat?.previewText || "",
      requestMessageFrom: chat?.requestMessageFrom || chat?.initiatedBy || "",
      requestCreatedAt: chat?.requestCreatedAt || chat?.lastUpdatedAt,
      lastActivityAt: chat?.lastUpdatedAt,
      previewText: chat?.previewText || chat?.requestMessage || "",
      unreadCount: normalizeInteger(chat?.unreadCount, 0),
    });
  }

  for (const request of snapshot?.pingReceivedRequests || []) {
    upsertCandidate({
      chatId: request?.chatPk,
      status: "pending",
      initiatedBy: request?.initiatedBy,
      isGroup: Boolean(request?.isGroup),
      counterpartId: request?.counterpartId,
      counterpartName: request?.title,
      requestMessage: request?.discussionTopic || "",
      requestMessageFrom: request?.initiatedBy || "",
      requestCreatedAt: request?.lastUpdatedAt,
      lastActivityAt: request?.lastUpdatedAt,
      previewText: request?.discussionTopic || "",
      unreadCount: request?.isInitiatedByMe ? 0 : 1,
    });
  }

  for (const request of snapshot?.pingAcceptedRequests || []) {
    upsertCandidate({
      chatId: request?.chatPk,
      status: "accepted",
      initiatedBy: request?.initiatedBy,
      isGroup: Boolean(request?.isGroup),
      counterpartId: request?.counterpartId,
      counterpartName: request?.title,
      requestMessage: request?.discussionTopic || "",
      requestMessageFrom: request?.initiatedBy || "",
      requestCreatedAt: request?.lastUpdatedAt,
      lastActivityAt: request?.lastUpdatedAt,
      previewText: request?.discussionTopic || "",
      unreadCount: 0,
    });
  }

  for (const chat of snapshot?.pastChats || []) {
    upsertCandidate({
      chatId: chat?.chatPk,
      status: "accepted",
      initiatedBy: "",
      isGroup: Boolean(chat?.isGroup),
      counterpartId: chat?.counterpartId,
      counterpartName: chat?.title || chat?.name,
      requestMessage: "",
      requestMessageFrom: "",
      requestCreatedAt: chat?.lastUpdatedAt,
      lastActivityAt: chat?.lastUpdatedAt,
      previewText: chat?.previewText || "",
      unreadCount: 0,
    });
  }

  return Array.from(byChatId.values());
}

async function getProjectionRow(userId, chatId) {
  const getFn = client?.models?.UserInboxItem?.get;
  if (typeof getFn === "function") {
    const getResp = await getFn({
      user_id: userId,
      chat_id: chatId,
    });
    const getErrors = getResp?.errors ?? [];
    if (getErrors.length) {
      throw new Error(getErrors.map((entry) => entry?.message).join(", "));
    }
    return getResp?.data ?? null;
  }

  const listResp = await client.models.UserInboxItem.list({
    filter: {
      user_id: { eq: userId },
      chat_id: { eq: chatId },
    },
    limit: 1,
  });
  const listErrors = listResp?.errors ?? [];
  if (listErrors.length) {
    throw new Error(listErrors.map((entry) => entry?.message).join(", "));
  }
  return listResp?.data?.[0] ?? null;
}

function buildProjectionWritePayload({
  userId,
  existing,
  candidate,
}) {
  const nowIso = new Date().toISOString();
  const lastActivityAt =
    maxIso(candidate?.lastActivityAt, existing?.last_activity_at) || nowIso;

  return {
    user_id: userId,
    chat_id: candidate.chatId,
    status: normalizeText(candidate.status || existing?.status || "accepted") || "accepted",
    initiated_by: normalizeText(candidate.initiatedBy) || normalizeText(existing?.initiated_by) || null,
    is_group:
      typeof candidate?.isGroup === "boolean"
        ? candidate.isGroup
        : Boolean(existing?.is_group),
    counterpart_id:
      normalizeText(candidate.counterpartId) || normalizeText(existing?.counterpart_id) || null,
    counterpart_name:
      normalizeText(candidate.counterpartName) || normalizeText(existing?.counterpart_name) || null,
    request_message:
      normalizeText(candidate.requestMessage) || normalizeText(existing?.request_message) || null,
    request_message_from:
      normalizeText(candidate.requestMessageFrom) ||
      normalizeText(existing?.request_message_from) ||
      null,
    request_created_at:
      normalizeText(candidate.requestCreatedAt) ||
      normalizeText(existing?.request_created_at) ||
      lastActivityAt,
    last_activity_at: lastActivityAt,
    preview_text:
      normalizeText(candidate.previewText) || normalizeText(existing?.preview_text) || null,
    last_message_from:
      normalizeText(candidate.lastMessageFrom) || normalizeText(existing?.last_message_from) || null,
    unread_count:
      normalizeInteger(
        candidate.unreadCount,
        normalizeInteger(existing?.unread_count, 0)
      ),
    updated_at: nowIso,
  };
}

function isProjectionDuplicateError(error) {
  const normalized = normalizeText(error).toLowerCase();
  return normalized.includes("already exists") || normalized.includes("duplicate");
}

async function upsertProjectionBackfillRow(userId, candidate) {
  const existing = await getProjectionRow(userId, candidate.chatId);
  const payload = buildProjectionWritePayload({
    userId,
    existing,
    candidate,
  });

  if (existing) {
    const updateResp = await client.models.UserInboxItem.update(payload);
    const updateErrors = updateResp?.errors ?? [];
    if (updateErrors.length) {
      throw new Error(updateErrors.map((entry) => entry?.message).join(", "));
    }
    return true;
  }

  const createResp = await client.models.UserInboxItem.create(payload);
  const createErrors = createResp?.errors ?? [];
  if (!createErrors.length) {
    return true;
  }

  const createErrorText = createErrors.map((entry) => entry?.message).join(", ");
  if (!isProjectionDuplicateError(createErrorText)) {
    throw new Error(createErrorText);
  }

  const latestExisting = await getProjectionRow(userId, candidate.chatId);
  if (!latestExisting) return false;
  const retryPayload = buildProjectionWritePayload({
    userId,
    existing: latestExisting,
    candidate,
  });
  const updateResp = await client.models.UserInboxItem.update(retryPayload);
  const updateErrors = updateResp?.errors ?? [];
  if (updateErrors.length) {
    throw new Error(updateErrors.map((entry) => entry?.message).join(", "));
  }
  return true;
}

async function backfillInboxProjectionFromSnapshot({ userId, snapshot }) {
  const normalizedUserId = normalizeText(userId);
  if (!normalizedUserId || !hasInboxProjectionModel()) return;
  if (projectionBackfillAttemptedByUserId.has(normalizedUserId)) return;
  projectionBackfillAttemptedByUserId.add(normalizedUserId);

  const candidates = collectProjectionBackfillCandidates(snapshot);
  if (!candidates.length) return;

  try {
    await Promise.all(
      candidates.map((candidate) => upsertProjectionBackfillRow(normalizedUserId, candidate))
    );
  } catch (error) {
    console.error("Inbox projection backfill failed:", error);
  }
}

async function fetchCommunicationInboxSnapshotFromProjection({
  userId,
  selectedChatPk = null,
}) {
  const mySenderId = normalizeText(userId);
  if (!mySenderId) return buildEmptySnapshot(userId);
  if (!hasInboxProjectionModel()) return null;

  const rows = dedupeInboxRowsByChatId(await listAllUserInboxItems(mySenderId), mySenderId);
  if (!rows.length) return null;

  const selectedChatCandidate = normalizeText(selectedChatPk);
  const selectedPresent = selectedChatCandidate
    ? rows.some((row) => normalizeText(row?.chat_id) === selectedChatCandidate)
    : false;
  if (selectedChatCandidate && !selectedPresent) {
    return null;
  }

  const hiddenConversationIds = await listHiddenConversationsForUser(mySenderId);
  const counterpartIds = rows
    .map((row) => normalizeText(row?.counterpart_id))
    .filter(Boolean);
  const profileSummaryByUserId = await getProfileSummaries(counterpartIds);
  const presenceByUserId = await listPresenceByUserIds(counterpartIds);
  const nowMs = Date.now();

  const activeChats = [];
  const pingReceivedRequests = [];
  const pingAcceptedRequests = [];
  const pastChats = [];
  const expiredPendingChatIds = new Set();

  for (const row of rows) {
    const chatPk = normalizeText(row?.chat_id);
    if (!chatPk) continue;

    const isSelectedCandidate = selectedChatCandidate && chatPk === selectedChatCandidate;
    if (hiddenConversationIds.has(chatPk) && !isSelectedCandidate) continue;

    const status = normalizeText(row?.status || "accepted") || "accepted";
    const initiatedBy = normalizeText(row?.initiated_by);
    const isInitiatedByMe = initiatedBy === mySenderId;
    const isGroupChat = Boolean(row?.is_group);
    const lastUpdatedAt = normalizeText(
      row?.last_activity_at || row?.updated_at || row?.updatedAt || row?.createdAt || ""
    );
    const requestMessage = normalizeText(row?.request_message || row?.preview_text || "");
    const requestMessageFrom = normalizeText(row?.request_message_from || initiatedBy || "");
    const requestCreatedAt = normalizeText(row?.request_created_at || lastUpdatedAt || "");
    const counterpartId = normalizeText(row?.counterpart_id) || null;
    const counterpartProfile = counterpartId
      ? profileSummaryByUserId.get(counterpartId)
      : null;
    const title =
      normalizeText(row?.counterpart_name) ||
      counterpartProfile?.profileName ||
      "Chat";
    const counterpartPresence = counterpartId
      ? presenceByUserId.get(counterpartId) || null
      : null;
    const lastSeenAt = counterpartPresence?.last_seen_at || null;
    const presenceStatus = isUserOnline(lastSeenAt) ? "online" : "offline";
    const projectionStateRow = {
      ...toConversationStateRowFromInboxRow(row),
      request_created_at: requestCreatedAt,
    };
    if (isPendingRequestExpired(projectionStateRow, nowMs)) {
      if (!isGroupChat) {
        expiredPendingChatIds.add(chatPk);
      }
      continue;
    }

    if (isActiveListConversation(projectionStateRow, mySenderId, nowMs)) {
      const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
      activeChats.push({
        chatPk,
        title,
        isGroup: isGroupChat,
        lastUpdatedAt,
        timeLabel: formatTimeLabel(lastUpdatedAt),
        avatar: safeInitials(title),
        profilePicture: counterpartProfile?.profilePicture || "",
        unreadCount:
          status === "pending" && !isInitiatedByMe
            ? 1
            : normalizeInteger(row?.unread_count, 0),
        previewText: normalizeText(row?.preview_text || requestMessage),
        status,
        initiatedBy,
        isInitiatedByMe,
        requestMessage,
        requestMessageFrom,
        requestCreatedAt,
        counterpartId: counterpartId || null,
        counterpartProfile: profileRecord,
        lastSeenAt,
        presenceStatus,
      });
    }

    if (shouldShowPingPending(projectionStateRow, mySenderId, nowMs)) {
      const requestActivityAt = normalizeText(row?.request_created_at || lastUpdatedAt);
      const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
      pingReceivedRequests.push({
        chatPk,
        title,
        isGroup: isGroupChat,
        lastUpdatedAt: requestActivityAt,
        timeLabel: formatTimeLabel(requestActivityAt),
        avatar: safeInitials(title),
        profilePicture: counterpartProfile?.profilePicture || "",
        status,
        discussionTopic: requestMessage,
        initiatedBy,
        isInitiatedByMe,
        counterpartId: counterpartId || null,
        counterpartProfile: profileRecord,
      });
    }

    if (status === "accepted" && isInitiatedByMe && isWithinHours(lastUpdatedAt, nowMs, 24)) {
      const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
      pingAcceptedRequests.push({
        chatPk,
        title,
        isGroup: isGroupChat,
        lastUpdatedAt,
        timeLabel: formatTimeLabel(lastUpdatedAt),
        avatar: safeInitials(title),
        profilePicture: counterpartProfile?.profilePicture || "",
        status: "accepted",
        discussionTopic: requestMessage,
        initiatedBy: mySenderId,
        isInitiatedByMe: true,
        counterpartId: counterpartId || null,
        counterpartProfile: profileRecord,
      });
    }

    if (isPastListConversation(projectionStateRow, nowMs)) {
      const profileRecord = buildCounterpartProfile(counterpartId, counterpartProfile, title);
      pastChats.push({
        id: chatPk,
        chatPk,
        name: title,
        title,
        isGroup: isGroupChat,
        lastUpdatedAt,
        timestamp: formatTimeLabel(lastUpdatedAt),
        timeLabel: formatTimeLabel(lastUpdatedAt),
        avatar: safeInitials(title),
        profilePicture: counterpartProfile?.profilePicture || "",
        lastMessage: "",
        previewText: normalizeText(row?.preview_text || ""),
        counterpartId: counterpartId || null,
        counterpartProfile: profileRecord,
      });
    }
  }

  activeChats.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));
  pingReceivedRequests.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));
  pingAcceptedRequests.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));
  pastChats.sort((a, b) => sortDescString(a.lastUpdatedAt, b.lastUpdatedAt));

  if (expiredPendingChatIds.size > 0) {
    void cleanupExpiredPendingChats(Array.from(expiredPendingChatIds));
  }

  return {
    userId: mySenderId,
    latestSyncedAt: new Date().toISOString(),
    activeChats,
    pendingRequests: [],
    pingReceivedRequests,
    pingAcceptedRequests,
    pastChats,
  };
}

async function fetchCommunicationInboxSnapshot({ userId, selectedChatPk = null }) {
  const mySenderId = normalizeText(userId);
  if (!mySenderId) return buildEmptySnapshot(userId);

  if (hasInboxProjectionModel()) {
    try {
      const projectionSnapshot = await fetchCommunicationInboxSnapshotFromProjection({
        userId: mySenderId,
        selectedChatPk,
      });
      if (projectionSnapshot) {
        return projectionSnapshot;
      }
    } catch (error) {
      console.error("Failed to load UserInboxItem projection snapshot:", error);
    }
  }

  const legacySnapshot = await fetchCommunicationInboxSnapshotLegacy({
    userId: mySenderId,
    selectedChatPk,
  });

  if (hasInboxProjectionModel()) {
    void backfillInboxProjectionFromSnapshot({
      userId: mySenderId,
      snapshot: legacySnapshot,
    });
  }

  return legacySnapshot;
}

export function getCachedCommunicationInboxSnapshot(userId) {
  const normalizedUserId = normalizeText(userId);
  if (!normalizedUserId) return null;
  return snapshotCacheByUserId.get(normalizedUserId)?.snapshot || null;
}

export function invalidateCommunicationInboxSnapshot(userId = null) {
  const normalizedUserId = normalizeText(userId);
  if (!normalizedUserId) {
    snapshotCacheByUserId.clear();
    projectionBackfillAttemptedByUserId.clear();
    return;
  }
  snapshotCacheByUserId.delete(normalizedUserId);
  projectionBackfillAttemptedByUserId.delete(normalizedUserId);
}

export async function loadCommunicationInboxSnapshot({
  userId,
  selectedChatPk = null,
  force = false,
  maxAgeMs = 4_000,
} = {}) {
  const normalizedUserId = normalizeText(userId);
  if (!normalizedUserId) return buildEmptySnapshot(userId);

  const nowMs = Date.now();
  const normalizedMaxAgeMs = Math.max(0, Number(maxAgeMs) || 0);
  const normalizedSelectedChatPk = normalizeText(selectedChatPk);
  const cachedEntry = snapshotCacheByUserId.get(normalizedUserId) || null;

  if (cachedEntry) {
    const ageMs = nowMs - cachedEntry.updatedAtMs;
    const recentlyForced = ageMs <= MAX_FORCE_REFRESH_PER_USER_MS;
    const cacheFreshEnough = ageMs <= normalizedMaxAgeMs;
    // A cached snapshot doesn't include hidden chats — those are only
    // surfaced when a caller passes selectedChatPk so the fetcher
    // synthesizes the row. If the caller is asking about a chat that
    // isn't in the cache, refetch instead of returning a snapshot that
    // would lock them out of their own (hidden) conversation.
    const cacheCoversSelected =
      !normalizedSelectedChatPk || snapshotIncludesChatPk(cachedEntry.snapshot, normalizedSelectedChatPk);

    if (
      cacheCoversSelected &&
      ((force && recentlyForced) || (!force && cacheFreshEnough))
    ) {
      return cachedEntry.snapshot;
    }
  }

  const existingInFlight = inFlightByUserId.get(normalizedUserId);
  if (existingInFlight && !normalizedSelectedChatPk) return existingInFlight;

  const requestPromise = fetchCommunicationInboxSnapshot({
    userId: normalizedUserId,
    selectedChatPk,
  })
    .then((snapshot) => {
      snapshotCacheByUserId.set(normalizedUserId, {
        updatedAtMs: Date.now(),
        snapshot,
      });
      return snapshot;
    })
    .catch((error) => {
      if (cachedEntry?.snapshot) {
        console.error("Using cached communication snapshot after fetch failure:", error);
        return cachedEntry.snapshot;
      }
      throw error;
    })
    .finally(() => {
      inFlightByUserId.delete(normalizedUserId);
    });

  inFlightByUserId.set(normalizedUserId, requestPromise);
  return requestPromise;
}
